import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Save, MessageSquare, Clock, Link, AlertCircle, Upload, Image, X } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { decode } from 'base64-arraybuffer';

interface ServiceConfig {
  wechat_work_link: string;
  work_hours_start_morning: string;
  work_hours_end_morning: string;
  work_hours_start_afternoon: string;
  work_hours_end_afternoon: string;
  rest_time_tip: string;
  chat_button_text: string;
  notification_email: string;
  customer_service_qr_code: string;
  customer_service_guide_text: string;
  customer_service_popup_title: string;
}

const defaultConfig: ServiceConfig = {
  wechat_work_link: '',
  work_hours_start_morning: '09:00',
  work_hours_end_morning: '12:00',
  work_hours_start_afternoon: '14:00',
  work_hours_end_afternoon: '21:00',
  rest_time_tip: '当前为非工作时间，请留言，我们会尽快回复您',
  chat_button_text: '咨询客服',
  notification_email: '',
  customer_service_qr_code: '',
  customer_service_guide_text: '扫码添加客服微信，快速咨询考级/课程问题',
  customer_service_popup_title: '联系客服',
};

export default function CustomerService() {
  const [config, setConfig] = useState<ServiceConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .eq('id', 'default')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setConfig({
          ...defaultConfig,
          ...data,
        });
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('site_config')
        .upsert({
          id: 'default',
          ...config,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'id',
        });

      if (error) throw error;

      setMessage({ type: 'success', text: '配置保存成功！' });
    } catch (error) {
      
      setMessage({ type: 'error', text: '保存失败，请重试' });
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key: keyof ServiceConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '请上传图片文件' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = (event.target?.result as string).split(',')[1];
        const fileName = `qr-code-${Date.now()}.${file.name.split('.').pop()}`;

        const { data, error } = await supabase.storage
          .from('customer-service')
          .upload(fileName, decode(base64), {
            contentType: file.type,
            upsert: true,
          });

        if (error) throw error;

        const { data: urlData } = supabase.storage
          .from('customer-service')
          .getPublicUrl(fileName);

        updateConfig('customer_service_qr_code', urlData.publicUrl);
        setMessage({ type: 'success', text: '二维码上传成功！' });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      
      setMessage({ type: 'error', text: '上传失败，请重试' });
    } finally {
      setUploading(false);
    }
  };

  const removeQrCode = () => {
    updateConfig('customer_service_qr_code', '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">客服设置</h1>
        <p className="text-gray-500 mt-1">配置客服系统的工作时间、二维码、引导文案等参数</p>
      </div>

      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-xl ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {message.text}
          </div>
        </motion.div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Image className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">客服微信二维码</h2>
              <p className="text-sm text-gray-500">上传客服微信二维码，用户扫码添加客服</p>
            </div>
          </div>

          <div className="mt-4">
            {config.customer_service_qr_code ? (
              <div className="relative inline-block">
                <img
                  src={config.customer_service_qr_code}
                  alt="客服二维码"
                  className="w-48 h-48 object-contain border border-gray-200 rounded-lg"
                  loading="lazy"
                />
                <button
                  onClick={removeQrCode}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">点击上传二维码</span>
                <span className="text-xs text-gray-400 mt-1">支持 JPG、PNG 格式</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            {uploading && (
              <div className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                上传中...
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">弹窗文案设置</h2>
              <p className="text-sm text-gray-500">自定义客服弹窗的标题和引导文案</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">弹窗标题</label>
              <input
                type="text"
                value={config.customer_service_popup_title}
                onChange={(e) => updateConfig('customer_service_popup_title', e.target.value)}
                placeholder="联系客服"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">引导文案</label>
              <textarea
                value={config.customer_service_guide_text}
                onChange={(e) => updateConfig('customer_service_guide_text', e.target.value)}
                rows={2}
                placeholder="扫码添加客服微信，快速咨询考级/课程问题"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">工作时间设置</h2>
              <p className="text-sm text-gray-500">设置客服在线时间段，非工作时间显示提示</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">上午时段</label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={config.work_hours_start_morning}
                  onChange={(e) => updateConfig('work_hours_start_morning', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                <span className="text-gray-400">至</span>
                <input
                  type="time"
                  value={config.work_hours_end_morning}
                  onChange={(e) => updateConfig('work_hours_end_morning', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">下午时段</label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={config.work_hours_start_afternoon}
                  onChange={(e) => updateConfig('work_hours_start_afternoon', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                <span className="text-gray-400">至</span>
                <input
                  type="time"
                  value={config.work_hours_end_afternoon}
                  onChange={(e) => updateConfig('work_hours_end_afternoon', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">提示文案</h2>
              <p className="text-sm text-gray-500">自定义按钮文字和非工作时间提示</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">悬浮按钮文字</label>
              <input
                type="text"
                value={config.chat_button_text}
                onChange={(e) => updateConfig('chat_button_text', e.target.value)}
                placeholder="咨询客服"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">非工作时间提示</label>
              <textarea
                value={config.rest_time_tip}
                onChange={(e) => updateConfig('rest_time_tip', e.target.value)}
                rows={2}
                placeholder="当前为非工作时间，请留言..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
              />
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Link className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">企业微信链接（可选）</h2>
              <p className="text-sm text-gray-500">如需跳转到企业微信，请填写链接（移动端生效）</p>
            </div>
          </div>

          <div>
            <input
              type="url"
              value={config.wechat_work_link}
              onChange={(e) => updateConfig('wechat_work_link', e.target.value)}
              placeholder="https://work.weixin.qq.com/..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              保存中...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              保存设置
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
}
